import { useState } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UseFormReturn } from "react-hook-form";
import { PatchPolicyFormValues } from "../../schema";
import { SeverityRemediationSection } from "../../vulnerabilities/SeverityRemediationSection";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert } from "lucide-react";

interface VulnerabilitiesTabProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function VulnerabilitiesTab({ form }: VulnerabilitiesTabProps) {
  const [enabled, setEnabled] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Vulnerability Remediation</h3>
          <Badge variant="outline" className="text-xs">Automated Patching</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Automatically patch vulnerabilities based on severity and timeframes
        </p>

        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="text-base">Enable Automatic Remediation</FormLabel>
            <FormDescription>
              Automatically deploy patches for detected vulnerabilities
            </FormDescription>
          </div>
          <FormControl>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </FormControl>
        </FormItem>
      </div>

      {enabled && (
        <div className="space-y-6 animate-fade-in">
          <div className="border-t pt-6">
            <h4 className="text-sm font-semibold mb-4">Severity-Based Rules</h4>
            <p className="text-sm text-muted-foreground mb-6">
              Configure automatic patching actions for each severity level
            </p>
            
            <div className="space-y-6">
              <SeverityRemediationSection
                form={form}
                severity="critical"
                cvssRange="9.0-10.0"
                barCount={4}
                colorClass="bg-red-500"
              />

              <SeverityRemediationSection
                form={form}
                severity="high"
                cvssRange="7.0-8.9"
                barCount={3}
                colorClass="bg-orange-500"
              />

              <SeverityRemediationSection
                form={form}
                severity="medium"
                cvssRange="4.0-6.9"
                barCount={2}
                colorClass="bg-yellow-500"
              />

              <SeverityRemediationSection
                form={form}
                severity="low"
                cvssRange="0.1-3.9"
                barCount={1}
                colorClass="bg-blue-500"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h4 className="text-sm font-semibold mb-6">Advanced Options</h4>
            
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="applyToActivelyExploitedOnly"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actively Exploited Only</FormLabel>
                      <FormDescription>
                        Only apply automatic remediation to vulnerabilities with active exploits
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vulnerabilityRestartBehavior"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Restart Behavior</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select restart behavior" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="defer_available">Allow Deferral</SelectItem>
                        <SelectItem value="force_immediate">Force Immediate Restart</SelectItem>
                        <SelectItem value="schedule_maintenance">Schedule During Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      How to handle device restarts after vulnerability patches
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vulnerabilityNotificationRecipients"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Recipients (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        value={field.value?.join(", ") || ""}
                        onChange={(e) => field.onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                        placeholder="email@example.com, another@example.com" 
                      />
                    </FormControl>
                    <FormDescription>
                      Comma-separated list of emails to notify when vulnerabilities are patched
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
